import { Box, Button, CircularProgress, Drawer, Typography } from "@mui/material";
import { DataTable } from "@/components/DesignSystem";
import { useNeverAuthenticatedIps } from "@/hooks/useDashboard.ts";
import type { QueriedIp } from "@/types";
import { formatSnapshotFreshness } from "@/utils/snapshotFreshness.ts";

const COLUMNS = [
  { header: "IP address", accessor: "ip_address" as const },
  { header: "Risk", accessor: (r: QueriedIp) => r.last_risk_overall, align: "right" as const },
  { header: "Queries", accessor: (r: QueriedIp) => r.query_count, align: "right" as const },
  {
    header: "Last queried",
    accessor: (r: QueriedIp) => new Date(r.last_queried_at * 1000).toLocaleString(),
    align: "right" as const,
  },
];

function DrawerContent({
  isLoading,
  rows,
}: {
  readonly isLoading: boolean;
  readonly rows: QueriedIp[];
}) {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (rows.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        No never-authenticated IPs in the latest snapshot.
      </Typography>
    );
  }
  return <DataTable columns={COLUMNS} rows={rows} getRowKey={(r) => r.ip_address} />;
}

export default function NeverAuthenticatedDrawer({
  open,
  asOf,
  onClose,
}: {
  readonly open: boolean;
  readonly asOf?: string | null;
  readonly onClose: () => void;
}) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useNeverAuthenticatedIps(open);
  const rows: QueriedIp[] = (data?.pages ?? []).flatMap((p) => p.items);
  const freshness = formatSnapshotFreshness(asOf);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: { xs: "100%", sm: 520 } } } }}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Never authenticated
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
          IPs queried at your perimeter that never went on to authenticate.
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 2, fontStyle: "italic" }}>
          {freshness ? `From the latest snapshot · as of ${freshness}` : "From the latest snapshot"}
        </Typography>
        <DrawerContent isLoading={isLoading} rows={rows} />
        {hasNextPage && (
          <Button onClick={() => fetchNextPage()} disabled={isFetchingNextPage} sx={{ mt: 2 }}>
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        )}
      </Box>
    </Drawer>
  );
}
